import type { CSSProperties } from "react";
import type { EnvironmentMaterial, Point } from "../types";
import type { MaterialReaction } from "../lib/living-environment";

export interface RenderedMaterialRegion {
  id: string;
  material: EnvironmentMaterial;
  polygons: Point[][];
  opacity: number;
  reaction: MaterialReaction;
}

interface Props {
  regions: RenderedMaterialRegion[];
  bounds: { left: number; top: number; width: number; height: number };
  projectionMask?: string;
  paused?: boolean;
}

const MATERIAL_COLORS: Record<EnvironmentMaterial, string> = {
  ground: "#75654d",
  vegetation: "#4f813d",
  wood: "#7a4b2d",
  stone: "#85888a",
  metal: "#aab4bd",
  ice: "#b9e9f3",
  snow: "#e8f3f7",
  mud: "#5f4632",
  lava: "#f06424",
};

const points = (polygon: Point[]) => polygon.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");

function centroid(polygons: Point[][]): Point {
  const all = polygons.flat();
  if (!all.length) return { x: 0, y: 0 };
  return { x: all.reduce((sum, point) => sum + point.x, 0) / all.length, y: all.reduce((sum, point) => sum + point.y, 0) / all.length };
}

export function LivingEnvironment({ regions, bounds, projectionMask, paused = false }: Props) {
  if (!regions.length || bounds.width <= 0 || bounds.height <= 0) return null;
  return <svg className={`living-environment${paused ? " paused" : ""}`} style={{ ...bounds, maskImage: projectionMask, WebkitMaskImage: projectionMask, maskSize: "100% 100%", WebkitMaskSize: "100% 100%" }} viewBox={`0 0 ${bounds.width} ${bounds.height}`} data-material-count={regions.length} data-projection-mask={projectionMask ? "active" : "none"} aria-hidden="true">
    <defs>
      <linearGradient id="tbltop-wet-shine" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#d9f5ff" stopOpacity="0" /><stop offset=".42" stopColor="#eaffff" stopOpacity=".72" /><stop offset=".55" stopColor="#ffffff" stopOpacity=".1" /><stop offset="1" stopColor="#a4d7ed" stopOpacity="0" /></linearGradient>
      <radialGradient id="tbltop-heat-glow"><stop offset="0" stopColor="#ffbd55" stopOpacity=".86" /><stop offset=".46" stopColor="#ff5b22" stopOpacity=".34" /><stop offset="1" stopColor="#e42c18" stopOpacity="0" /></radialGradient>
      <pattern id="tbltop-snow-grain" width="23" height="19" patternUnits="userSpaceOnUse"><circle cx="4" cy="5" r="1.4" fill="#fff" opacity=".72" /><circle cx="17" cy="12" r="2.1" fill="#dff4ff" opacity=".55" /><circle cx="11" cy="2" r=".9" fill="#fff" opacity=".65" /></pattern>
      <pattern id="tbltop-vegetation-grain" width="18" height="16" patternUnits="userSpaceOnUse"><path d="M3 11Q7 2 10 11Q7 14 3 11M12 5Q15 1 17 6Q14 9 12 5" fill="#9fc765" opacity=".38" /></pattern>
    </defs>
    {regions.map((region) => {
      const center = centroid(region.polygons);
      const reaction = region.reaction;
      const style = {
        "--material-wind": reaction.windMotion.toFixed(3),
        "--material-heat": reaction.heat.toFixed(3),
        "--material-steam": reaction.steam.toFixed(3),
      } as CSSProperties;
      return <g key={region.id} className={`material-region material-${region.material}`} style={style} data-material={region.material} data-wetness={reaction.wetness.toFixed(3)} data-snow={reaction.snowCover.toFixed(3)} data-heat={reaction.heat.toFixed(3)} data-reflection={reaction.reflection.toFixed(3)}>
        {region.polygons.map((polygon, index) => <polygon key={`base-${index}`} points={points(polygon)} fill={MATERIAL_COLORS[region.material]} opacity={Math.max(0.02, region.opacity * 0.24)} />)}
        {reaction.wetness > 0.01 ? region.polygons.map((polygon, index) => <polygon key={`wet-${index}`} className="material-wet-shine" points={points(polygon)} fill="url(#tbltop-wet-shine)" opacity={reaction.wetness * (0.24 + reaction.reflection * 0.34)} />) : null}
        {reaction.snowCover > 0.01 ? region.polygons.map((polygon, index) => <polygon key={`snow-${index}`} className="material-snow-cover" points={points(polygon)} fill="url(#tbltop-snow-grain)" opacity={reaction.snowCover * 0.82} />) : null}
        {reaction.glow > 0.01 ? <circle className="material-heat-glow" cx={center.x} cy={center.y} r={Math.max(24, Math.min(bounds.width, bounds.height) * 0.12)} fill="url(#tbltop-heat-glow)" opacity={reaction.glow * 0.62} /> : null}
        {reaction.steam > 0.04 ? <g className="material-steam" opacity={reaction.steam * 0.68}><circle cx={center.x - 13} cy={center.y + 4} r="8" /><circle cx={center.x + 3} cy={center.y - 7} r="11" /><circle cx={center.x + 18} cy={center.y + 2} r="7" /></g> : null}
      </g>;
    })}
  </svg>;
}
